import { TUI_CONFIRM } from '@taiga-ui/kit';
import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  RequestDealDetailsWithInstaller,
  RequestDTO,
  RequestStatus,
  RequestStatuses,
  RequestStatusesServiceTranslations,
  RequestStatusesServiceTranslationsPayload,
  RequestStatusesTranslations,
  RequestStatusesTranslationsPayload,
} from '@core/types';
import { Nullable } from '@core/helpers/nullable';
import {
  delay,
  EMPTY,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
} from 'rxjs';
import { AccountService } from '../account/account.service';
import { WarehouseService } from '../warehouse/warehouse.service';
import { XAlertService } from '../x-alert.service';
import { RequestDevicesService } from './request-devices.service';
import { RequestEntitiesService } from './request-entities.service';
import { RequestsApiService } from './requests-api.service';
import { TuiDialogService } from '@taiga-ui/core';
import {
  RequestFormDealDetailsComponent,
  RequestFormStatusCancelReasonComponent,
  RequestFormStatusEditReasonComponent,
} from '@features/requests';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { RequestModel } from '@core/models';
import { format } from 'date-fns';
import { catchError } from 'rxjs/operators';
import { RequestDealDetailsService } from './request-deal-details.service';
import {
  RequestStatusChangeReasonForm,
  RequestStatusDealDetailsForm,
  RequestStatusFormData,
} from '@features/requests/request-form/components/request-form-status/types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { REQUEST_STATUSES_TRANSLATION_KEY } from '@core/constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import {
  isStatusChangeForbiddenForCustomer,
  REQUEST_STATUS_ERROR_CODES,
  REQUEST_STATUS_NAMES,
} from '@outkeep/shared';

type ReleaseDateForCheckError =
  | RequestStatusesServiceTranslations.NoReleaseDateForCheckErrorMessage
  | RequestStatusesServiceTranslations.ReleaseDateInPastForCheckErrorMessage;

@Injectable({
  providedIn: 'root',
})
export class RequestStatusesService extends RequestEntitiesService<
  RequestStatus[]
> {
  private readonly requestsApiService = inject(RequestsApiService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly accountService = inject(AccountService);
  readonly $user = this.accountService.$currentUser;
  private readonly alertService$ = inject(XAlertService);
  private readonly dialogService$ = inject(TuiDialogService);
  private readonly dealDetailsService = inject(RequestDealDetailsService);
  private readonly devicesService = inject(RequestDevicesService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  override readonly destroyRef = inject(DestroyRef);

  private locale = this.outkeepTranslateService.getLocaleStr();
  private translates: RequestStatusesServiceTranslationsPayload &
    RequestStatusesTranslationsPayload = {
    [RequestStatusesServiceTranslations.UnableChangeStatus]:
      'Невозможно перевести заявку в статус',
    [RequestStatusesServiceTranslations.NoReleaseDatetimeErrorMessage]:
      'отсутствует дата и время выдачи.',
    [RequestStatusesServiceTranslations.ActualDeliveryAddressNotConfirmedErrorMessage]:
      'актуальный адрес выдачи не подтвержден.',
    [RequestStatusesServiceTranslations.ReleaseTimeInFutureErrorMessage]:
      'дата выдачи в будущем',
    [RequestStatusesServiceTranslations.ReleaseDateInPastForCheckErrorMessage]:
      'Нельзя отправить на проверку заявку с прошедшей датой выдачи',
    [RequestStatusesServiceTranslations.NoReleaseDateForCheckErrorMessage]:
      'Нельзя отправить на проверку заявку без даты выдачи',
    [RequestStatusesServiceTranslations.Today]: 'Сегодня',
    [RequestStatusesServiceTranslations.ProductsAreNotCompletedErrorMessage]:
      'услуги не были помечены как завершенные.',
    [RequestStatusesServiceTranslations.DeviceInstallQualityFailedErrorMessage]:
      'устройства не прошли проверку качества установки (дождитесь первичной/повторной проверки, в случае ошибки перезапустите проверку или проверьте устройства вручную).',
    [RequestStatusesServiceTranslations.OnlyCreatorCanChangeStatusFromNew]:
      'Статус новой заявки может изменить только её создатель.',
    [RequestStatusesServiceTranslations.StatusChangeForbiddenForCustomer]:
      'изменить статус выполненной или выданной заявки может только исполнитель. Для изменения обратитесь к вашему менеджеру.',
    [RequestStatusesServiceTranslations.OnlyCurrentSlsCanChangeDealDetails]:
      'Сейчас детали сделки не добавлены, и только СЛС текущей заявки может добавить эти детали и перевести заявку в этот статус.',
    [RequestStatusesServiceTranslations.ReasonQueryToEditRequest]:
      'Причина запроса на редактирование:',
    [RequestStatusesServiceTranslations.DialogChangeStatus]:
      'Вы уверены что хотите изменить статус?',
    [RequestStatusesServiceTranslations.DialogChangeStatusYes]: 'Да',
    [RequestStatusesServiceTranslations.DialogChangeStatusNo]: 'Нет',
    [RequestStatusesServiceTranslations.NoCancellationInitiatorError]:
      'Для смены статуса на "Отменена" необходимо указать инициатора отмены.',
    [RequestStatusesServiceTranslations.NoReasonQueryToEditInitiatorError]:
      'Для смены статуса на "Запрос на редактирование" необходимо указать причину запроса на редактирование.',
    [RequestStatusesServiceTranslations.DialogNotAllRequestsCanBeChanged]:
      'Некоторые заявки не могут быть переведены в данный статус! Вы хотите перевести только доступные заявки, а остальные убрать из выделения?',
    [RequestStatusesServiceTranslations.DialogNotAllRequestsCanBeChangedYes]:
      'Да',
    [RequestStatusesServiceTranslations.DialogNotAllRequestsCanBeChangedNo]:
      'Нет',
    [RequestStatusesServiceTranslations.AllSelectedRequestsCannotBeChangedError]:
      'Ни одна из выбранных заявок не может быть переведена в данный статус',
    [RequestStatusesServiceTranslations.ErrorHappenedTellDevs]:
      'Произошла ошибка! Сообщите разработчикам',
    [RequestStatusesServiceTranslations.ChangeStatuses]: 'Назначение статусов',
    [RequestStatusesServiceTranslations.ChangeStatusSuccess]:
      'Статусы назначены; Выполняем повторную загрузку изменённых заявок',
    [RequestStatusesServiceTranslations.Warn]: 'Внимание!',
    [RequestStatusesServiceTranslations.InStatus]: 'в статус',
    [RequestStatusesServiceTranslations.FromStatus]: 'из статуса',
    [RequestStatusesServiceTranslations.UnableChangeRequest]:
      'Невозможно перевести заявку',
    [RequestStatusesServiceTranslations.NoDealDetails]:
      'отсутствуют описания сделки',
    [RequestStatusesServiceTranslations.NoPrimaryDocumentation]:
      'в описании сделки не указана первичная документация',
    [RequestStatusesTranslations.New]: REQUEST_STATUS_NAMES.NEW,
    [RequestStatusesTranslations.Completed]: REQUEST_STATUS_NAMES.COMPLETED,
    [RequestStatusesTranslations.RequestForEditing]:
      REQUEST_STATUS_NAMES.REQUEST_RETURN,
    [RequestStatusesTranslations.Editing]: REQUEST_STATUS_NAMES.IN_EDIT,
    [RequestStatusesTranslations.CheckCompleted]:
      REQUEST_STATUS_NAMES.CHECK_FINISHED,
    [RequestStatusesTranslations.RequestWaitingForCheck]:
      REQUEST_STATUS_NAMES.WORK,
    [RequestStatusesTranslations.InProgress]: REQUEST_STATUS_NAMES.IN_WORK,
    [RequestStatusesTranslations.Issued]: REQUEST_STATUS_NAMES.DONE,
    [RequestStatusesTranslations.SpecialistAssigned]:
      REQUEST_STATUS_NAMES.SLS_WAS_APPOINTED,
    [RequestStatusesTranslations.RequestError]: REQUEST_STATUS_NAMES.MISTAKE,
    [RequestStatusesTranslations.Canceled]: REQUEST_STATUS_NAMES.CANCELLED,
    [RequestStatusesTranslations.Rejected]: REQUEST_STATUS_NAMES.REJECTED,
    [RequestStatusesTranslations.RejectedLocal]: REQUEST_STATUS_NAMES.REJECTED,
  };

  readonly $request = this.requestService.$request;
  private $selectedRequests = this.requestService.selectedRequests;
  private selectedRequestsDealDetails: RequestDealDetailsWithInstaller[] = [];

  readonly source$ = () => {
    return this.requestsApiService
      .getRequestStatuses$()
      .pipe(
        map(statuses =>
          statuses.sort(
            (prev: RequestStatus, next: RequestStatus) =>
              (prev?.sort || 999) - (next?.sort || 999),
          ),
        ),
      );
  };
  constructor() {
    super();
    this.outkeepTranslateService
      .onLangChange()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.locale = this.outkeepTranslateService.getLocaleStr();
      });
    this.outkeepTranslateService
      .translate(
        Object.values(
          Object.assign(
            RequestStatusesServiceTranslations,
            RequestStatusesTranslations,
          ),
        ),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  showErrorAlert$(message: string, params = {}): Observable<null> {
    return of(
      this.alertService$.showMessage(message, {
        appearance: 'negative',
        ...params,
      }),
    ).pipe(
      delay(1500),
      map(() => null),
    );
  }

  showWarningAlert$(message: string, params = {}): Observable<null> {
    return of(
      this.alertService$.showMessage(message, {
        appearance: 'warning',
        ...params,
      }),
    ).pipe(
      delay(1500),
      map(() => null),
    );
  }

  showNoReleaseDateTimeErrorMessage$(status: RequestStatus): Observable<null> {
    // Невозможно перевести заявку в статус "<span style='color:${status.color}'>${status.name}</span>": отсутствует дата и время выдачи.
    const message = `${this.translates[RequestStatusesServiceTranslations.UnableChangeStatus]} "<span style='color:${status.color}'>${this.statusNameTranslate(status.id)}</span>": ${this.translates[RequestStatusesServiceTranslations.NoReleaseDatetimeErrorMessage]}`;

    return this.showErrorAlert$(message);
  }

  showActualDeliveryAddressIsNotConfirmedErrorMessage$(
    status: RequestStatus,
  ): Observable<null> {
    // Невозможно перевести заявку в статус "<span style='color:${status.color}'>${status.name}</span>": актуальный адрес выдачи не подтвержден.
    const message = `${this.translates[RequestStatusesServiceTranslations.UnableChangeStatus]} "<span style='color:${status.color}'>${this.statusNameTranslate(status.id)}</span>": ${this.translates[RequestStatusesServiceTranslations.ActualDeliveryAddressNotConfirmedErrorMessage]}`;

    return this.showErrorAlert$(message);
  }

  showReleaseDateTimeInFutureErrorMessage$(
    status: RequestStatus,
  ): Observable<null> {
    const releaseDate = this.$request().releaseDate;

    if (!releaseDate) throw new Error('releaseDate should be defined');

    const releaseDateOnlyDay = new Date(releaseDate);
    const todayDay = new Date();

    releaseDateOnlyDay.setHours(0, 0, 0, 0);
    todayDay.setHours(0, 0, 0, 0);

    // `Невозможно перевести заявку в статус "<span style='color:</span>": дата выдачи в будущем ${releaseDateOnlyDay.toLocaleDateString()}. Сегодня ${todayDay.toLocaleDateString()}.`
    const message = `${this.translates[RequestStatusesServiceTranslations.UnableChangeStatus]} "<span style='color:${
      status.color
    }'>${this.statusNameTranslate(
      status.id,
    )}</span>": ${this.translates[RequestStatusesServiceTranslations.ReleaseTimeInFutureErrorMessage]} ${releaseDateOnlyDay.toLocaleDateString(this.locale)}. ${this.translates[RequestStatusesServiceTranslations.Today]} ${todayDay.toLocaleDateString(this.locale)}.`;

    return this.showErrorAlert$(message);
  }

  showReleaseDateForCheckErrorMessage$(
    error: ReleaseDateForCheckError,
  ): Observable<null> {
    return this.showWarningAlert$(this.translates[error]);
  }

  showProductsAreNotCompletedErrorMessage(
    status: RequestStatus,
  ): Observable<null> {
    // Невозможно перевести заявку в статус "<span style='color:${status.color}'>${status.name}</span>": услуги не были помечены как завершенные.
    const message = `${this.translates[RequestStatusesServiceTranslations.UnableChangeStatus]} "<span style='color:${status.color}'>${this.statusNameTranslate(status.id)}</span>": ${this.translates[RequestStatusesServiceTranslations.ProductsAreNotCompletedErrorMessage]}`;

    return this.showErrorAlert$(message);
  }

  showDeviceInstallQualityFailed(status: RequestStatus): Observable<null> {
    //  `Невозможно перевести заявку в статус "<span style='color:${status.color}'>${this.translates[status.id]}</span>": устройства не прошли проверку качества установки (дождитесь первичной/повторной проверки, в случае ошибки перезапустите проверку или проверьте устройства вручную).`;
    const message = `${this.translates[RequestStatusesServiceTranslations.UnableChangeStatus]} "<span style='color:${status.color}'>${this.statusNameTranslate(status.id)}</span>": ${this.translates[RequestStatusesServiceTranslations.DeviceInstallQualityFailedErrorMessage]}`;

    return this.showErrorAlert$(message);
  }

  showOnlyCreatorCanChangeStatusFromNewErrorMessage$(): Observable<null> {
    // Статус новой заявки может изменить только её создатель.
    const message =
      this.translates[
        RequestStatusesServiceTranslations.OnlyCreatorCanChangeStatusFromNew
      ];

    return this.showErrorAlert$(message);
  }

  showOnlyCurrentSlsCanChangeDealDetails$() {
    // Сейчас детали сделки не добавлены, и только СЛС текущей заявки может добавить эти детали и перевести заявку в этот статус.
    const message =
      this.translates[
        RequestStatusesServiceTranslations.OnlyCurrentSlsCanChangeDealDetails
      ];

    return this.showErrorAlert$(message);
  }

  checkCanWeSetCompletedStatus$(
    status: RequestStatus,
  ): Observable<Nullable<boolean>> {
    const request = this.$request();

    if (request.needToCheckRequiredFields && !request.areAllProductsCompleted) {
      return this.showProductsAreNotCompletedErrorMessage(status);
    }

    if (!request.isReleaseDateTimeFilledInRequest) {
      return this.showNoReleaseDateTimeErrorMessage$(status);
    }
    if (!request.isReleaseDateTimeNotInFuture) {
      return this.showReleaseDateTimeInFutureErrorMessage$(status);
    }
    if (!request.actualDeliveryAddress) {
      return this.showActualDeliveryAddressIsNotConfirmedErrorMessage$(status);
    }
    if (this.checkOnlyCreatorCanChangeRequestStatusFromNew$()) {
      return this.showOnlyCreatorCanChangeStatusFromNewErrorMessage$();
    }

    return of(true);
  }

  checkDealDetailsWasCreated$(): Observable<Nullable<boolean>> {
    return this.dealDetailsService.requestDealDetails$.pipe(
      switchMap(dealDetails => {
        if (dealDetails?.details) {
          return of(true);
        }

        if (!dealDetails?.isCurrentUserInstaller) {
          return this.showOnlyCurrentSlsCanChangeDealDetails$();
        }

        return of(false);
      }),
    );
  }

  checkInstallQualityPassed$(
    _status: RequestStatus,
  ): Observable<Nullable<boolean>> {
    // * блокировка смены статуса по этим проверкам временно отключена
    return of(true);

    // return this.devicesService.entities$.pipe(
    //   switchMap(devices =>
    //     forkJoin(
    //       devices.map(device =>
    //         this.devicesService.getDeviceInstallQualityStatus$(device.id),
    //       ),
    //     ),
    //   ),
    //   switchMap(statuses => {
    //     if (statuses.every(status => status.status === true)) {
    //       return of(true);
    //     }

    //     return this.showDeviceInstallQualityFailed(status);
    //   }),
    // );
  }

  checkCanWeSetGivenStatus$(
    status: RequestStatus,
  ): Observable<Nullable<boolean>> {
    const request = this.$request();

    if (!request.actualDeliveryAddress || !request.actualAddressConfirmed) {
      return this.showActualDeliveryAddressIsNotConfirmedErrorMessage$(status);
    }
    if (!request.isReleaseDateTimeFilledInRequest) {
      return this.showNoReleaseDateTimeErrorMessage$(status);
    }
    if (!request.isReleaseDateTimeNotInFuture) {
      return this.showReleaseDateTimeInFutureErrorMessage$(status);
    }
    if (this.checkOnlyCreatorCanChangeRequestStatusFromNew$()) {
      return this.showOnlyCreatorCanChangeStatusFromNewErrorMessage$();
    }

    return of(true);
  }

  commonChecksForChangeStatus$(
    status: RequestStatus,
  ): Observable<Nullable<boolean>> {
    if (this.checkOnlyCreatorCanChangeRequestStatusFromNew$()) {
      return this.showOnlyCreatorCanChangeStatusFromNewErrorMessage$();
    }

    const releaseDateError =
      status.id === RequestStatuses.RequestIsWaitingForCheck
        ? this.getReleaseDateForCheckError(this.$request().releaseDate)
        : null;

    if (releaseDateError) {
      return this.showReleaseDateForCheckErrorMessage$(releaseDateError);
    }

    return of(true);
  }

  tryToChangeCompletedStatus$(
    status: RequestStatus,
    cancelCallback$: () => Observable<null>,
  ): Observable<Nullable<RequestStatusDealDetailsForm> | boolean> {
    return this.checkCanWeSetCompletedStatus$(status).pipe(
      switchMap(canContinue => (canContinue ? of(null) : cancelCallback$())),
      switchMap(this.checkInstallQualityPassed$.bind(this, status)),
      switchMap(canContinue => (canContinue ? of(null) : cancelCallback$())),
      switchMap(this.checkDealDetailsWasCreated$.bind(this)),
      switchMap(wasCreated =>
        this.afterCheckDealDetailsWasCreated$.call(
          this,
          wasCreated,
          cancelCallback$,
        ),
      ),
    );
  }

  tryToChangeGivenStatus$(
    status: RequestStatus,
    cancelCallback$: () => Observable<null>,
  ): Observable<Nullable<RequestStatusDealDetailsForm> | boolean> {
    return this.checkCanWeSetGivenStatus$(status).pipe(
      switchMap(canContinue => (canContinue ? of(null) : cancelCallback$())),
      switchMap(this.checkDealDetailsWasCreated$.bind(this)),
      switchMap(wasCreated =>
        this.afterCheckDealDetailsWasCreated$.call(
          this,
          wasCreated,
          cancelCallback$,
        ),
      ),
    );
  }

  tryToChangeAnyOtherStatus$(
    status: RequestStatus,
    cancelCallback$: () => Observable<null>,
  ): Observable<boolean> {
    return this.commonChecksForChangeStatus$(status).pipe(
      switchMap(canContinue => (canContinue ? of(null) : cancelCallback$())),
      switchMap(this.openConfirmDialog$.bind(this)),
    );
  }

  private afterCheckDealDetailsWasCreated$(
    wasCreated: Nullable<boolean>,
    cancelCallback$: () => Observable<null>,
  ) {
    if (wasCreated === null) {
      return cancelCallback$();
    }
    if (wasCreated) {
      return this.openConfirmDialog$();
    }

    return this.openDealDetailsDialog$();
  }

  changeRequestStatus$(
    statusId: RequestStatuses,
    additionalData: {
      cancelReason?: string;
      requestToEditReason?: string;
    } = {},
    forceChange = false,
  ): Observable<RequestDTO[]> {
    return this.requestsApiService
      .changeRequestsStatus$(
        [this.$request().id],
        statusId,
        additionalData,
        forceChange,
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          const backendErrorMessage = err.error?.message;
          const errorMessage =
            err.error?.code ===
            REQUEST_STATUS_ERROR_CODES.NO_PRIMARY_DOCUMENTATION
              ? this.translates[
                  RequestStatusesServiceTranslations.NoPrimaryDocumentation
                ]
              : backendErrorMessage ||
                this.translates[
                  RequestStatusesServiceTranslations.ErrorHappenedTellDevs
                ];

          console.error('changeRequestStatus$ error: ', err);
          this.alertService$.showMessage(errorMessage, {
            appearance: 'negative',
          });
          throw Error(errorMessage);
        }),
      );
  }

  tryToSetCompletedStatus$({
    formData,
  }: RequestStatusFormData): Observable<null> {
    return (
      formData
        ? this.dealDetailsService.createRequestDealDetails$(formData)
        : of(null)
    ).pipe(
      switchMap(() => this.changeRequestStatus$(RequestStatuses.Completed)),
      switchMap(() => this.sellAllDevicesInWarehouseIfNeed$()),
    );
  }

  tryToSetEditQueryStatus$({
    formData,
  }: RequestStatusFormData): Observable<void> {
    const { reason } = formData;

    return this.changeRequestStatus$(RequestStatuses.EditQuery, {
      requestToEditReason: reason,
    }).pipe(
      switchMap(() =>
        this.requestsApiService.createRequestComment$(
          this.$request().id,
          `${
            this.translates[
              RequestStatusesServiceTranslations.ReasonQueryToEditRequest
            ]
          } ${reason}`,
        ),
      ),
    );
  }

  tryToSetCancelledStatus$({
    formData,
  }: RequestStatusFormData): Observable<void> {
    const { reason } = formData;

    return this.changeRequestStatus$(RequestStatuses.Cancelled, {
      cancelReason: reason,
    }).pipe(
      switchMap(() =>
        this.requestsApiService.setRequestCancelReason$(
          this.$request().id,
          reason,
        ),
      ),
    );
  }

  tryToSetGivenStatus$({
    formData,
  }: RequestStatusFormData): Observable<null | RequestDTO[]> {
    return (
      formData
        ? this.dealDetailsService.createRequestDealDetails$(formData)
        : of(null)
    ).pipe(
      switchMap(() => {
        return this.changeRequestStatus$(RequestStatuses.Given);
      }),
    );
  }

  private sellAllDevicesInWarehouseIfNeed$(): Observable<null> {
    const devicesSIM1 = (this.$request().devices || [])
      .map(device => device.name)
      .filter(Boolean);
    const installerId = this.$request().idOfInstaller;

    if (!installerId || !devicesSIM1.length) {
      return of(null);
    }

    return from(devicesSIM1).pipe(
      switchMap(sim1 => this.warehouseService.sellDevice$(installerId, sim1)),
      switchMap(result => {
        console.debug('sellDevice$ result: ', result);
        // if (!result.isSuccess) {
        //   return this.showErrorAlert$(result.errorText, {
        //     autoClose: false,
        //   });
        // }

        return of(null);
      }),
    );
  }

  sellAllDevicesInWarehouseIfNeedMulti$(
    requests: RequestModel[],
  ): Observable<null> {
    const allDevicesSIM1 = requests.flatMap(request =>
      (request.devices || []).map(device => device.name).filter(Boolean),
    );
    const installerIds = requests
      .map(request => request.idOfInstaller)
      .filter(Boolean);

    if (!installerIds.length || !allDevicesSIM1.length) {
      return of(null);
    }

    return from(allDevicesSIM1).pipe(
      switchMap(sim1 => {
        const installerId = installerIds.find(id => id);

        return this.warehouseService.sellDevice$(installerId!, sim1);
      }),
      switchMap(result => {
        console.debug('sellAllDevicesInWarehouseIfNeedMulti$ result: ', result);
        // if (!result.isSuccess) {
        //   return this.showErrorAlert$(result.errorText, {
        //     autoClose: false,
        //   });
        // }

        return of(null);
      }),
    );
  }

  checkOnlyCreatorCanChangeRequestStatusFromNew$(): boolean {
    const request = this.$request();

    return (
      request.statusId!.name === REQUEST_STATUS_NAMES.NEW &&
      this.$user()!.id !== request.createdId.id &&
      !request.createRequestParentId
    );
  }

  // DIALOGS
  openConfirmDialog$(): Observable<boolean> {
    return this.dialogService$.open<boolean>(TUI_CONFIRM, {
      size: 's',
      closable: false,
      dismissible: false,
      data: {
        content:
          this.translates[
            RequestStatusesServiceTranslations.DialogChangeStatus
          ],
        yes: this.translates[
          RequestStatusesServiceTranslations.DialogChangeStatusYes
        ],
        no: this.translates[
          RequestStatusesServiceTranslations.DialogChangeStatusNo
        ],
      },
    });
  }

  openCancelDialog$(): Observable<Nullable<RequestStatusChangeReasonForm>> {
    return this.dialogService$.open<Nullable<RequestStatusChangeReasonForm>>(
      new PolymorpheusComponent(RequestFormStatusCancelReasonComponent),
      {
        size: 's',
        closable: false,
        dismissible: false,
      },
    );
  }

  openDealDetailsDialog$(): Observable<Nullable<RequestStatusDealDetailsForm>> {
    return this.dialogService$.open<Nullable<RequestStatusDealDetailsForm>>(
      new PolymorpheusComponent(RequestFormDealDetailsComponent),
      {
        appearance: 'fullscreen',
        closable: false,
        dismissible: false,
      },
    );
  }

  openEditDialog$(): Observable<Nullable<RequestStatusChangeReasonForm>> {
    return this.dialogService$.open<Nullable<RequestStatusChangeReasonForm>>(
      new PolymorpheusComponent(RequestFormStatusEditReasonComponent),
      {
        size: 's',
        closable: false,
        dismissible: false,
      },
    );
  }

  // END DIALOGS

  // GROUP ACTIONS
  applyGroupStatus(
    newStatus: RequestStatus,
    viewObserver: Subject<never>,
  ): Observable<any> {
    const statusName = newStatus.name;
    const requestsAllowedToSetStatus: RequestModel[] = [];
    const additionalData: {
      cancelReason?: string;
      requestToEditReason?: string;
    } = {};

    const selectedRequests = this.$selectedRequests();

    return this.openConfirmDialog$().pipe(
      switchMap(isConfirmed => {
        if (!isConfirmed) {
          viewObserver.complete();

          return EMPTY;
        }
        // Если статус "Выполнено" или "Выдано", то нужны данные о сделках для проверки наличия
        switch (statusName) {
          case REQUEST_STATUS_NAMES.COMPLETED:
          case REQUEST_STATUS_NAMES.DONE:
            return this.requestsApiService.getRequestsDealDetails(
              selectedRequests.map(({ id }) => id),
            );
          // Если статус "Отменена", то нужна причина отмены
          case REQUEST_STATUS_NAMES.CANCELLED:
            return this.openCancelDialog$();
          // Если статус "Запрос на редактирование", то нужна причина запроса на редактирование
          case REQUEST_STATUS_NAMES.REQUEST_RETURN:
            return this.openEditDialog$();
          default:
            return of(true);
        }
      }),
      switchMap(data => {
        switch (statusName) {
          case REQUEST_STATUS_NAMES.COMPLETED:
          case REQUEST_STATUS_NAMES.DONE:
            this.selectedRequestsDealDetails =
              data as RequestDealDetailsWithInstaller[];
            break;
          case REQUEST_STATUS_NAMES.CANCELLED:
            if (!data) {
              return this.showErrorAlert$(
                this.translates[
                  RequestStatusesServiceTranslations
                    .NoCancellationInitiatorError
                ],
              );
            }

            additionalData['cancelReason'] = (
              data as RequestStatusChangeReasonForm
            ).reason!;
            break;
          case REQUEST_STATUS_NAMES.REQUEST_RETURN:
            if (!data) {
              return this.showErrorAlert$(
                this.translates[
                  RequestStatusesServiceTranslations
                    .NoReasonQueryToEditInitiatorError
                ],
              );
            }
            additionalData['requestToEditReason'] = (
              data as RequestStatusChangeReasonForm
            ).reason!;
            break;
        }
        for (const request of selectedRequests) {
          if (this.checkStatus(request, newStatus)) {
            requestsAllowedToSetStatus.push(request);
          }
        }

        return of(true);
      }),

      // * блокировка смены статуса по проверке качества установки временно отключена
      // switchMap(data => {
      //   // проверяем контроль качества установки

      //   if (statusName === 'Выполнено') {
      //     return forkJoin(
      //       requestsAllowedToSetStatus.map(request =>
      //         forkJoin([
      //           of(request),
      //           forkJoin([
      //             // хак, чтобы при пустом массиве devices
      //             // не получался пустой forkJoin, который никогда не эмитит
      //             of(true),
      //             ...request.devices.map(device => {
      //               return this.devicesService
      //                 .getDeviceInstallQualityStatus$(device.id)
      //                 .pipe(map(status => status.status));
      //             }),
      //           ]),
      //         ]),
      //       ),
      //     ).pipe(
      //       switchMap(requests => {
      //         requestsAllowedToSetStatus = requests
      //           .filter(statuses => {
      //             const someFailed = statuses[1].some(
      //               status => status !== true,
      //             );

      //             if (someFailed)
      //               this.alertService$.warn(
      //                 'Внимание',
      //                 `Невозможно перевести заявку <b>"${statuses[0].requestNumber || statuses[0].id}"</b> в статус "<span style='color:${newStatus.color}'>${newStatus.name}</span>": устройства не прошли проверку качества установки.`,
      //                 5000,
      //               );

      //             return !someFailed;
      //           })
      //           .map(reqStatus => reqStatus[0]);

      //         return of(data);
      //       }),
      //     );
      //   }

      //   return of(data);
      // }),
      switchMap(data => {
        // проверяем, можно ли перевести все / какие-то / никакие
        // из выбранных заявок в новый статус

        if (requestsAllowedToSetStatus.length > 0) {
          if (requestsAllowedToSetStatus.length !== selectedRequests.length) {
            return this.dialogService$.open<boolean>(TUI_CONFIRM, {
              size: 's',
              closable: false,
              dismissible: false,
              data: {
                content:
                  this.translates[
                    RequestStatusesServiceTranslations
                      .DialogNotAllRequestsCanBeChanged
                  ],
                yes: this.translates[
                  RequestStatusesServiceTranslations
                    .DialogNotAllRequestsCanBeChangedYes
                ],
                no: this.translates[
                  RequestStatusesServiceTranslations
                    .DialogNotAllRequestsCanBeChangedNo
                ],
              },
            });
          }
        } else {
          viewObserver.complete();

          return this.showErrorAlert$(
            this.translates[
              RequestStatusesServiceTranslations
                .AllSelectedRequestsCannotBeChangedError
            ],
            { autoClose: false },
          );
        }

        return of(data);
      }),
      switchMap(canContinue => {
        if (!canContinue) {
          viewObserver.complete();

          return EMPTY;
        }
        // remove not allowed requests from selection
        const allowedRequestIds = new Set(
          requestsAllowedToSetStatus.map(({ id }) => id),
        );

        this.requestService.unselectFromSelectedRequestsIds(
          selectedRequests.filter(({ id }) => !allowedRequestIds.has(id)),
        );

        return this.requestsApiService.changeRequestsStatus$(
          requestsAllowedToSetStatus.map(({ id }) => id),
          newStatus.id,
          additionalData,
          true, // принудительная смена статуса для групповых действий (пользуются логисты)
          true, // групповое действие: не блокировать сменой по проверке первичной документации
        );
      }),
      catchError(() => {
        this.alertService$.error(
          this.translates[
            RequestStatusesServiceTranslations.ErrorHappenedTellDevs
          ],
          this.translates[RequestStatusesServiceTranslations.ChangeStatuses],
          false,
        );
        viewObserver.complete();

        return EMPTY;
      }),
      switchMap(updatedRequests => {
        if (!updatedRequests) {
          viewObserver.complete();

          return EMPTY;
        }

        if (statusName === REQUEST_STATUS_NAMES.COMPLETED) {
          return this.sellAllDevicesInWarehouseIfNeedMulti$(
            updatedRequests,
          ).pipe(map(() => updatedRequests));
        }

        return of(updatedRequests);
      }),
      switchMap(updatedRequests => {
        if (Array.isArray(updatedRequests)) {
          this.alertService$.success(
            this.translates[
              RequestStatusesServiceTranslations.ChangeStatusSuccess
            ],
          );

          // update all loaded requests
          return this.requestService.reloadSelectedRequests$();
        }

        return of(null);
      }),
    );
  }

  /**
   * Запрещён ли текущему пользователю перевод заявки из статуса currentStatusId
   * в статус newStatusId (SYST-3029). Пока пользователь не загружен, он считается заказчиком.
   */
  isStatusChangeForbidden(
    currentStatusId: number | null | undefined,
    newStatusId: number | null | undefined,
  ): boolean {
    return isStatusChangeForbiddenForCustomer(
      currentStatusId,
      newStatusId,
      this.$user()?.isDefaultCounterpartyEmployee === true,
    );
  }

  checkStatus(request: RequestModel, status: RequestStatus): boolean {
    if (!status || !request) return false;
    const statusName = status.name;
    const statusId = status.id;
    const {
      releaseDate,
      actualDeliveryAddress,
      requestNumber,
      id,
      statusId: requestStatus,
    } = request;
    // невалидную дату не форматируем: date-fns бросает RangeError и прерывает групповую смену статуса
    const releaseDateOnlyDay = this.isValidReleaseDate(releaseDate)
      ? format(releaseDate, 'yyyy-mm-dd')
      : null;

    if (
      requestStatus!.name === REQUEST_STATUS_NAMES.NEW &&
      this.$user()!.id !== request.createdId.id &&
      !request.createRequestParentId
    ) {
      // Невозможно перевести заявку  <b>"${requestNumber || id}"</b> из статуса "<span style='color:${requestStatus!.color}'>${requestStatus!.name}</span>": статус новой заявки может изменить только её создатель.,
      this.alertService$.warn(
        this.translates[RequestStatusesServiceTranslations.Warn],
        `${
          this.translates[
            RequestStatusesServiceTranslations.UnableChangeRequest
          ]
        }  <b>"${requestNumber || id}"</b> ${
          this.translates[RequestStatusesServiceTranslations.FromStatus]
        } "<span style='color:${requestStatus!.color}'>${statusName}</span>": ${
          this.translates[
            RequestStatusesServiceTranslations.OnlyCreatorCanChangeStatusFromNew
          ]
        }`,
        5000,
      );

      return false;
    }

    if (this.isStatusChangeForbidden(requestStatus?.id, statusId)) {
      // Невозможно перевести заявку <b>"${requestNumber || id}"</b> из статуса "<span style='color:${requestStatus!.color}'>${requestStatus!.name}</span>" в статус "...": изменить статус выполненной или выданной заявки может только исполнитель
      this.alertService$.warn(
        this.translates[RequestStatusesServiceTranslations.Warn],
        `${
          this.translates[
            RequestStatusesServiceTranslations.UnableChangeRequest
          ]
        } <b>"${requestNumber || id}"</b> ${
          this.translates[RequestStatusesServiceTranslations.FromStatus]
        } "<span style='color:${requestStatus!.color}'>${requestStatus!.name}</span>" ${
          this.translates[RequestStatusesServiceTranslations.InStatus]
        } "<span style='color:${status.color}'>${this.statusNameTranslate(statusId)}</span>": ${
          this.translates[
            RequestStatusesServiceTranslations.StatusChangeForbiddenForCustomer
          ]
        }`,
        5000,
      );

      return false;
    }

    const todayDay = format(new Date(), 'yyyy-mm-dd');

    if (statusName === REQUEST_STATUS_NAMES.DONE && !actualDeliveryAddress) {
      // Невозможно перевести заявку <b>"${requestNumber || id}"</b> в статус "<span style='color:${status.color}'>${status.name}</span>": актуальный адрес выдачи не подтвержден
      this.alertService$.warn(
        this.translates[RequestStatusesServiceTranslations.Warn],
        `${
          this.translates[
            RequestStatusesServiceTranslations.UnableChangeRequest
          ]
        } <b>"${requestNumber || id}"</b> ${
          this.translates[RequestStatusesServiceTranslations.InStatus]
        } "<span style='color:${status.color}'>${this.statusNameTranslate(statusId)}</span>": ${
          this.translates[
            RequestStatusesServiceTranslations
              .ActualDeliveryAddressNotConfirmedErrorMessage
          ]
        }`,
        5000,
      );

      return false;
    }

    const releaseDateError =
      statusId === RequestStatuses.RequestIsWaitingForCheck
        ? this.getReleaseDateForCheckError(releaseDate)
        : null;

    if (releaseDateError) {
      this.alertService$.warn(
        this.translates[RequestStatusesServiceTranslations.Warn],
        this.translates[releaseDateError],
        5000,
      );

      return false;
    }

    if (
      statusName === REQUEST_STATUS_NAMES.COMPLETED ||
      statusName === REQUEST_STATUS_NAMES.DONE
    ) {
      if (!releaseDate || isNaN(new Date(releaseDate).getTime())) {
        // Невозможно перевести заявку <b>"${requestNumber || id}"</b> в статус "<span style='color:${status.color}'>${status.name}</span>": отсутствует дата выдачи
        this.alertService$.warn(
          this.translates[RequestStatusesServiceTranslations.Warn],
          `${
            this.translates[
              RequestStatusesServiceTranslations.UnableChangeRequest
            ]
          } <b>"${requestNumber || id}"</b> ${
            this.translates[RequestStatusesServiceTranslations.InStatus]
          } "<span style='color:${status.color}'>${this.statusNameTranslate(statusId)}</span>": ${
            this.translates[
              RequestStatusesServiceTranslations.NoReleaseDatetimeErrorMessage
            ]
          }`,
          5000,
        );

        return false;
      } else if (releaseDateOnlyDay! > todayDay) {
        // Невозможно перевести заявку <b>"${requestNumber || id}"</b> в статус "<span style='color:${status.color}'>${status.name}</span>": дата выдачи в будущем ${new Date(releaseDate).toLocaleDateString()}. Сегодня ${new Date().toLocaleDateString()}.
        this.alertService$.warn(
          this.translates[RequestStatusesServiceTranslations.Warn],
          `${
            this.translates[
              RequestStatusesServiceTranslations.UnableChangeRequest
            ]
          } <b>"${requestNumber || id}"</b> ${
            this.translates[RequestStatusesServiceTranslations.InStatus]
          } "<span style='color:${status.color}'>${this.statusNameTranslate(statusId)}</span>": ${this.translates[RequestStatusesServiceTranslations.ReleaseTimeInFutureErrorMessage]} ${new Date(releaseDate).toLocaleDateString(this.locale)}}. ${this.translates[RequestStatusesServiceTranslations.Today]} ${new Date().toLocaleDateString(this.locale)}.`,
          5000,
        );

        return false;
      } else if (
        !this.selectedRequestsDealDetails.find(
          ({ requestId }) => requestId === id,
        )
      ) {
        // Невозможно перевести заявку <b>"${requestNumber || id}"</b> в статус "<span style='color:${status.color}'>${status.name}</span>": отсутствуют описания сделки

        this.alertService$.warn(
          this.translates[RequestStatusesServiceTranslations.Warn],
          `${
            this.translates[
              RequestStatusesServiceTranslations.UnableChangeRequest
            ]
          } <b>"${requestNumber || id}"</b> ${
            this.translates[RequestStatusesServiceTranslations.InStatus]
          } "<span style='color:${status.color}'>${this.statusNameTranslate(statusId)}</span>": ${
            this.translates[RequestStatusesServiceTranslations.NoDealDetails]
          }`,
          5000,
        );

        return false;
      }
    }

    if (
      statusName === REQUEST_STATUS_NAMES.COMPLETED &&
      request.needToCheckRequiredFields &&
      !request.areAllProductsCompleted
    ) {
      // Невозможно перевести заявку <b>"${requestNumber || id}"</b> в статус "<span style='color:${status.color}'>${status.name}</span>": услуги не были помечены как завершенные
      this.alertService$.warn(
        this.translates[RequestStatusesServiceTranslations.Warn],
        `${
          this.translates[
            RequestStatusesServiceTranslations.UnableChangeRequest
          ]
        } <b>"${requestNumber || id}"</b> ${
          this.translates[RequestStatusesServiceTranslations.InStatus]
        } "<span style='color:${status.color}'>${this.statusNameTranslate(statusId)}</span>": ${
          this.translates[
            RequestStatusesServiceTranslations
              .ProductsAreNotCompletedErrorMessage
          ]
        }`,
        5000,
      );

      return false;
    }

    return true;
  }

  // END GROUP ACTIONS

  /**
   * Проверка даты выдачи для перевода в "Заявка ждёт проверки":
   * не установленная дата равносильна прошедшей (SYST-3125)
   */
  private getReleaseDateForCheckError(
    releaseDate?: string,
  ): ReleaseDateForCheckError | null {
    if (!this.isValidReleaseDate(releaseDate)) {
      return RequestStatusesServiceTranslations.NoReleaseDateForCheckErrorMessage;
    }

    if (this.isReleaseDateInPast(releaseDate)) {
      return RequestStatusesServiceTranslations.ReleaseDateInPastForCheckErrorMessage;
    }

    return null;
  }

  private isValidReleaseDate(
    releaseDate?: string | null,
  ): releaseDate is string {
    return !!releaseDate?.trim() && !isNaN(new Date(releaseDate).getTime());
  }

  private isReleaseDateInPast(releaseDate: string): boolean {
    const releaseDateOnlyDay = new Date(releaseDate);
    const todayDay = new Date();

    releaseDateOnlyDay.setHours(0, 0, 0, 0);
    todayDay.setHours(0, 0, 0, 0);

    return releaseDateOnlyDay < todayDay;
  }

  private statusNameTranslate(statusId: number): string {
    return this.translates[
      `${REQUEST_STATUSES_TRANSLATION_KEY}.${statusId}` as RequestStatusesTranslations
    ];
  }
}
